import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { resolveImageUrl } from '../../services/api';
import WhatsAppAuthModal from '../shared/WhatsAppAuthModal';
import CustomerProfile from '../shared/CustomerProfile';
import {
  TrashIcon, ArrowLeftIcon, ShoppingCartIcon, HeartIcon,
  MapPinIcon, ChevronRightIcon, DevicePhoneMobileIcon,
  BoltIcon, CubeIcon, UserGroupIcon, CurrencyRupeeIcon,
  UserIcon, ArrowRightOnRectangleIcon, UserCircleIcon,
  PlusIcon, MinusIcon, ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

/* ── Profile Dropdown (same as Products) ─────────────────────────────────── */
const ProfileDropdown = ({ user, onLogout, onViewProfile }) => (
  <>
    <style>{`
      @keyframes pdIn2{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
      .pd2{position:absolute;top:calc(100% + 10px);right:0;background:white;border-radius:1.25rem;
           padding:.4rem;box-shadow:0 16px 40px rgba(0,0,0,.14);border:1px solid #f3f4f6;
           min-width:210px;z-index:200;animation:pdIn2 .2s ease-out;}
      .pd2-hd{padding:.75rem 1rem;border-bottom:1px solid #f3f4f6;margin-bottom:.25rem;}
      .pd2-av{width:2.5rem;height:2.5rem;background:linear-gradient(135deg,#10b981,#059669);
              border-radius:50%;display:flex;align-items:center;justify-content:center;
              color:white;font-size:1rem;font-weight:800;margin-bottom:.4rem;}
      .pd2-nm{font-weight:800;font-size:.92rem;color:#111827;}
      .pd2-ph{font-size:.72rem;color:#6b7280;margin-top:.1rem;}
      .pd2-it{display:flex;align-items:center;gap:.55rem;padding:.55rem .9rem;border-radius:.65rem;
              font-size:.85rem;font-weight:600;color:#374151;cursor:pointer;transition:background .18s;
              border:none;background:none;width:100%;font-family:'Nunito',sans-serif;}
      .pd2-it:hover{background:#f0fdf4;color:#059669;}
      .pd2-it.dng:hover{background:#fff1f2;color:#ef4444;}
      .pd2-it svg{width:.95rem;height:.95rem;flex-shrink:0;}
    `}</style>
    <div className="pd2">
      <div className="pd2-hd">
        <div className="pd2-av">{(user.name||'U').charAt(0).toUpperCase()}</div>
        <div className="pd2-nm">{user.name||'Customer'}</div>
        <div className="pd2-ph">📱 +{user.phone}</div>
      </div>
      <button className="pd2-it" onClick={onViewProfile}><ShoppingBagIcon/>My Orders</button>
      <button className="pd2-it" onClick={onViewProfile}><UserCircleIcon/>My Profile</button>
      <button className="pd2-it dng" onClick={onLogout}><ArrowRightOnRectangleIcon/>Logout</button>
    </div>
  </>
);

const Cart = () => {
  const navigate = useNavigate();
  const { cart, removeFromCart, updateQuantity, getCartTotal, clearCart, cartCount } = useCart();
  const { customer: currentUser, isLoggedIn, logout } = useCustomerAuth();

  const [scrolled,        setScrolled]        = useState(false);
  const [showProfileDrop, setShowProfileDrop] = useState(false);
  const [showAuthModal,   setShowAuthModal]   = useState(false);
  const [showProfile,     setShowProfile]     = useState(false);
  const [imgErrors,       setImgErrors]       = useState({});
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (!showProfileDrop) return;
    const fn = () => setShowProfileDrop(false);
    setTimeout(() => document.addEventListener('click', fn), 0);
    return () => document.removeEventListener('click', fn);
  }, [showProfileDrop]);

  const handleLogout = () => {
    logout();
    setShowProfileDrop(false);
    toast.success('Logged out 👋');
  };

  const handleAuthSuccess = (user) => {
    setShowAuthModal(false);
    toast.success(`Welcome, ${user.name||'there'}! 🥛`);
  };

  const handleQuantityChange = (productId, newQty) => {
    if (newQty < 1) {
      const item = cart.find(i => i.id === productId);
      removeFromCart(productId);
      if (item) toast.success(`${item.name} removed`);
    } else {
      updateQuantity(productId, newQty);
    }
  };

  const handleRemove = (productId, name) => {
    removeFromCart(productId);
    toast.success(`${name} removed`);
  };

  const handleCheckout = () => navigate('/checkout');

  const handleClearCart = () => {
    if (window.confirm('Clear your cart?')) { clearCart(); toast.success('Cart cleared'); }
  };

  const getImageSrc = (p) => {
    const raw = p?.image_url || p?.imageUrl || p?.image || null;
    return resolveImageUrl(raw);
  };

  const getEmoji = (p) => {
    const c = (p.category||'').toLowerCase(), n = (p.name||'').toLowerCase();
    if (c.includes('paneer')||n.includes('paneer')) return '🧀';
    if (c.includes('ghee')  ||n.includes('ghee'))   return '🫙';
    if (c.includes('butter')||n.includes('butter')) return '🧈';
    if (c.includes('buttermilk')||n.includes('buttermilk')) return '🧃';
    return '🥛';
  };

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap');
    *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
    body{font-family:'Nunito',sans-serif;background:#f9fafb;}

    /* ── HEADER (identical to Products) ── */
    .pr-hdr{position:sticky;top:0;z-index:50;background:rgba(255,255,255,.97);
            backdrop-filter:blur(12px);border-bottom:1px solid rgba(229,231,235,.6);
            transition:box-shadow .3s;width:100%;}
    .pr-hdr.sc{box-shadow:0 4px 20px rgba(0,0,0,.07);}
    .pr-hdr-in{display:flex;align-items:center;justify-content:space-between;
               height:54px;gap:.4rem;padding:0 .875rem;max-width:1280px;margin:0 auto;}
    @media(min-width:768px){.pr-hdr-in{height:64px;padding:0 1.5rem;}}
    .pr-logo{display:flex;align-items:center;cursor:pointer;flex-shrink:0;}
    .pr-logo img{height:34px;object-fit:contain;}
    @media(min-width:768px){.pr-logo img{height:42px;}}
    .pr-acts{display:flex;align-items:center;gap:.3rem;flex-shrink:0;}
    .pr-ib{width:34px;height:34px;display:flex;align-items:center;justify-content:center;
           border-radius:50%;border:none;background:#f3f4f6;color:#374151;cursor:pointer;
           transition:all .2s;flex-shrink:0;}
    .pr-ib:hover{background:#e5e7eb;color:#059669;}
    .pr-ib svg{width:1rem;height:1rem;}
    @media(min-width:768px){.pr-ib{width:38px;height:38px;}}
    .pr-cw{position:relative;display:inline-flex;}
    .pr-cb{position:absolute;top:-4px;right:-4px;width:.9rem;height:.9rem;background:#ef4444;
           color:white;font-size:.5rem;font-weight:800;border-radius:50%;
           display:flex;align-items:center;justify-content:center;border:2px solid white;}
    .pr-pw{position:relative;display:inline-flex;}
    .pr-pb{display:flex;align-items:center;gap:.3rem;background:#ecfdf5;
           border:1.5px solid #a7f3d0;color:#047857;font-weight:700;font-size:.75rem;
           padding:.28rem .65rem .28rem .28rem;border-radius:9999px;cursor:pointer;
           transition:all .2s;font-family:'Nunito',sans-serif;white-space:nowrap;}
    .pr-pb:hover{background:#d1fae5;}
    .pr-pav{width:1.5rem;height:1.5rem;background:linear-gradient(135deg,#10b981,#059669);
            border-radius:50%;display:flex;align-items:center;justify-content:center;
            color:white;font-size:.65rem;font-weight:800;flex-shrink:0;}
    .pr-pnm{display:none;max-width:65px;overflow:hidden;text-overflow:ellipsis;}
    @media(min-width:440px){.pr-pnm{display:block;}}
    .pr-lbtn{display:flex;align-items:center;gap:.28rem;background:white;color:#059669;
             border:1.5px solid #d1fae5;font-weight:700;font-size:.72rem;
             padding:.32rem .65rem;border-radius:9999px;cursor:pointer;
             transition:all .2s;white-space:nowrap;font-family:'Nunito',sans-serif;}
    .pr-lbtn:hover{background:#f0fdf4;}
    .pr-lbtn svg{width:.75rem;height:.75rem;}

    /* ── MAIN ── */
    .ct-wrap{max-width:1280px;margin:0 auto;padding:1.5rem .875rem;}
    @media(min-width:768px){.ct-wrap{padding:2rem 1.5rem;}}

    /* top bar */
    .ct-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:1.5rem;flex-wrap:wrap;gap:.5rem;}
    .ct-bk{display:flex;align-items:center;gap:.35rem;background:none;border:none;
           color:#6b7280;font-weight:600;font-size:.85rem;cursor:pointer;
           font-family:'Nunito',sans-serif;padding:.4rem .75rem;border-radius:9999px;
           transition:all .2s;}
    .ct-bk:hover{background:#f3f4f6;color:#059669;}
    .ct-bk svg{width:.9rem;height:.9rem;}
    .ct-clr{background:none;border:none;color:#ef4444;font-weight:600;font-size:.82rem;
            cursor:pointer;padding:.4rem .75rem;border-radius:9999px;
            font-family:'Nunito',sans-serif;transition:all .2s;}
    .ct-clr:hover{background:#fee2e2;}
    .ct-ttl{font-size:1.5rem;font-weight:900;
            background:linear-gradient(135deg,#059669,#0891b2);
            -webkit-background-clip:text;-webkit-text-fill-color:transparent;
            margin-bottom:1.5rem;}
    @media(min-width:768px){.ct-ttl{font-size:2rem;}}

    /* grid */
    .ct-grid{display:grid;grid-template-columns:1fr;gap:1.5rem;}
    @media(min-width:1024px){.ct-grid{grid-template-columns:2fr 1fr;}}

    /* items card */
    .ct-items{background:white;border-radius:1.25rem;border:1.5px solid #f3f4f6;overflow:hidden;
              box-shadow:0 4px 16px rgba(0,0,0,.04);}
    .ct-item{display:flex;flex-direction:column;padding:1rem;border-bottom:1.5px solid #f9fafb;gap:.85rem;}
    @media(min-width:480px){.ct-item{flex-direction:row;align-items:center;gap:1.1rem;}}
    .ct-item:last-child{border-bottom:none;}
    .ct-img{width:68px;height:68px;border-radius:.85rem;background:#f9fafb;
            border:1.5px solid #f3f4f6;display:flex;align-items:center;justify-content:center;
            overflow:hidden;flex-shrink:0;}
    .ct-img img{width:100%;height:100%;object-fit:contain;}
    .ct-img span{font-size:1.85rem;}
    .ct-info{flex:1;min-width:0;}
    .ct-name{font-size:.95rem;font-weight:800;color:#111827;margin-bottom:.2rem;
             white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
    .ct-desc{font-size:.75rem;color:#9ca3af;margin-bottom:.35rem;}
    .ct-price{display:flex;align-items:baseline;gap:.4rem;}
    .ct-total{font-size:1.1rem;font-weight:900;color:#059669;}
    .ct-unit{font-size:.72rem;color:#9ca3af;}
    .ct-acts{display:flex;align-items:center;gap:.6rem;flex-shrink:0;}
    .ct-qc{display:flex;align-items:center;border:2px solid #e5e7eb;border-radius:9999px;overflow:hidden;}
    .ct-qb{background:none;border:none;padding:.35rem .6rem;font-weight:700;color:#4b5563;
           cursor:pointer;transition:all .2s;display:flex;align-items:center;}
    .ct-qb:hover{background:#f3f4f6;color:#059669;}
    .ct-qn{padding:.35rem .6rem;font-weight:700;color:#111827;
           border-left:2px solid #e5e7eb;border-right:2px solid #e5e7eb;
           min-width:2rem;text-align:center;font-size:.9rem;}
    .ct-rm{color:#ef4444;background:none;border:none;padding:.45rem;border-radius:9999px;
           cursor:pointer;transition:all .2s;display:flex;align-items:center;}
    .ct-rm:hover{background:#fee2e2;}

    /* summary card */
    .ct-sum{background:white;border-radius:1.25rem;border:1.5px solid #f3f4f6;
            padding:1.35rem;box-shadow:0 4px 16px rgba(0,0,0,.04);}
    @media(min-width:1024px){.ct-sum{position:sticky;top:80px;}}
    .ct-sum-ttl{font-size:1rem;font-weight:900;color:#111827;margin-bottom:1.25rem;
                display:flex;align-items:center;gap:.45rem;}
    .ct-sum-ttl svg{width:1.1rem;height:1.1rem;color:#059669;}
    .ct-sum-row{display:flex;justify-content:space-between;margin-bottom:.75rem;
                font-size:.88rem;color:#6b7280;}
    .ct-sum-tot{display:flex;justify-content:space-between;padding-top:.85rem;
                border-top:2px solid #f3f4f6;font-size:1.15rem;font-weight:900;color:#111827;}
    .ct-sum-tot-amt{color:#059669;}
    .ct-co-btn{width:100%;padding:.85rem;background:linear-gradient(135deg,#059669,#10b981);
               color:white;border:none;border-radius:9999px;font-weight:800;font-size:1rem;
               margin-top:1.25rem;cursor:pointer;transition:all .3s;
               box-shadow:0 4px 12px rgba(5,150,105,.2);font-family:'Nunito',sans-serif;
               display:flex;align-items:center;justify-content:center;gap:.4rem;}
    .ct-co-btn:hover{transform:translateY(-2px);box-shadow:0 8px 18px rgba(5,150,105,.3);}
    .ct-free{text-align:center;margin-top:.75rem;color:#10b981;font-size:.8rem;
             font-weight:700;display:flex;align-items:center;justify-content:center;gap:.35rem;}
    .ct-free svg{width:.9rem;height:.9rem;}

    /* empty state */
    .ct-empty{max-width:28rem;margin:4rem auto;background:white;border-radius:1.5rem;
              padding:3rem 2rem;text-align:center;border:1.5px solid #f3f4f6;
              box-shadow:0 8px 24px rgba(0,0,0,.04);}
    .ct-empty svg{width:4rem;height:4rem;color:#d1d5db;margin:0 auto 1.25rem;}
    .ct-empty h2{font-size:1.25rem;font-weight:900;color:#111827;margin-bottom:.5rem;}
    .ct-empty p{font-size:.85rem;color:#9ca3af;margin-bottom:1.5rem;}
    .ct-empty-btn{background:linear-gradient(135deg,#10b981,#059669);color:white;border:none;
                  padding:.7rem 1.8rem;border-radius:9999px;font-weight:800;font-size:.9rem;
                  cursor:pointer;font-family:'Nunito',sans-serif;}

    /* trust bar */
    .ct-trust{background:#f9fafb;padding:1.35rem 0;border-top:1px solid #e5e7eb;margin-top:2.5rem;}
    .ct-trust-in{max-width:1280px;margin:0 auto;padding:0 .875rem;
                 display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:.85rem 1.75rem;}
    .ct-trust-item{display:flex;align-items:center;gap:.5rem;}
    .ct-trust-ico{width:1.65rem;height:1.65rem;border-radius:50%;
                  display:flex;align-items:center;justify-content:center;}
    .ct-trust-item span{font-size:.82rem;font-weight:600;color:#374151;}

    /* footer */
    .ct-footer{background:#0f172a;color:white;padding:3.5rem 0 2rem;}
    .ct-footer-in{max-width:1280px;margin:0 auto;padding:0 .875rem;}
    .ct-footer-grid{display:grid;grid-template-columns:1fr;gap:2rem;margin-bottom:2.5rem;}
    @media(min-width:640px){.ct-footer-grid{grid-template-columns:repeat(2,1fr);}}
    @media(min-width:1024px){.ct-footer-grid{grid-template-columns:2fr 1fr 1fr 1.5fr;gap:2.5rem;}}
    .ct-flogo{display:flex;align-items:center;margin-bottom:1rem;cursor:pointer;}
    .ct-flogo img{height:40px;object-fit:contain;}
    .ct-fabout{color:#94a3b8;line-height:1.7;font-size:.88rem;margin-bottom:1rem;}
    .ct-fsoc{display:flex;gap:.65rem;}
    .ct-fsoc a{width:2rem;height:2rem;background:#1e293b;border-radius:.55rem;
               display:flex;align-items:center;justify-content:center;
               color:white;text-decoration:none;transition:all .3s;}
    .ct-fsoc a:hover{background:#2563eb;transform:translateY(-2px);}
    .ct-fttl{font-size:.95rem;font-weight:700;color:white;margin-bottom:1rem;
             display:flex;align-items:center;gap:.35rem;}
    .ct-fttl svg{width:.9rem;height:.9rem;color:#ef4444;}
    .ct-flinks{list-style:none;}
    .ct-flinks li{margin-bottom:.6rem;}
    .ct-flinks a{color:#94a3b8;text-decoration:none;font-size:.88rem;
                 transition:all .2s;display:inline-flex;align-items:center;gap:.3rem;}
    .ct-flinks a:hover{color:white;transform:translateX(4px);}
    .ct-fcontact{list-style:none;}
    .ct-fcontact li{display:flex;align-items:flex-start;gap:.55rem;
                    color:#94a3b8;font-size:.88rem;margin-bottom:.75rem;}
    .ct-fcontact li svg{width:1rem;height:1rem;flex-shrink:0;margin-top:.1rem;}
    .ct-fnl{margin-top:.85rem;}
    .ct-fnl p{color:white;font-size:.88rem;font-weight:600;margin-bottom:.4rem;}
    .ct-fnl-row{display:flex;gap:.35rem;}
    .ct-fnl-row input{flex:1;padding:.55rem .85rem;border-radius:9999px;border:1px solid #334155;
                      background:#1e293b;color:white;font-size:.82rem;font-family:'Nunito',sans-serif;}
    .ct-fnl-row input::placeholder{color:#64748b;}
    .ct-fnl-row button{background:#2563eb;color:white;border:none;border-radius:9999px;
                       padding:.55rem .9rem;font-weight:700;font-size:.78rem;
                       cursor:pointer;font-family:'Nunito',sans-serif;}
    .ct-fbot{border-top:1px solid #1e293b;padding-top:1.75rem;display:flex;
             flex-direction:column;align-items:center;gap:.85rem;text-align:center;}
    @media(min-width:768px){.ct-fbot{flex-direction:row;justify-content:space-between;text-align:left;}}
    .ct-fcopy{color:#64748b;font-size:.78rem;}
    .ct-fbot-links{display:flex;flex-wrap:wrap;gap:1.25rem;justify-content:center;}
    .ct-fbot-links a{color:#94a3b8;text-decoration:none;font-size:.78rem;transition:color .2s;}
    .ct-fbot-links a:hover{color:white;}
    .ct-seo{color:#f59e0b !important;font-weight:700;display:inline-flex;align-items:center;gap:.3rem;}
    @keyframes hb{0%,100%{transform:scale(1)}25%,75%{transform:scale(1.15)}}
    .hb{animation:hb 1.4s ease-in-out infinite;}
  `;

  const EmptyCart = () => (
    <>
      <style>{css}</style>
      {showAuthModal && <WhatsAppAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      {showProfile   && <CustomerProfile  onClose={()=>setShowProfile(false)}/>}
      <div style={{minHeight:'100vh',background:'#f9fafb'}}>
        <Header/>
        <div className="ct-wrap">
          <div className="ct-empty">
            <ShoppingCartIcon/>
            <h2>Your cart is empty</h2>
            <p>Looks like you haven't added any products yet.</p>
            <button className="ct-empty-btn" onClick={()=>navigate('/products')}>Continue Shopping</button>
          </div>
        </div>
        <Footer/>
      </div>
    </>
  );

  const Header = () => (
    <header className={`pr-hdr${scrolled?' sc':''}`}>
      <div className="pr-hdr-in">
        <div className="pr-logo" onClick={()=>navigate('/')}>
          <img src="/images/logo.png" alt="Fresh Hills"
            onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/42?text=FH';}}/>
        </div>
        <div className="pr-acts">
          <div className="pr-cw">
            <button className="pr-ib" onClick={()=>navigate('/cart')}><ShoppingCartIcon/></button>
            {cartCount>0 && <span className="pr-cb">{cartCount>9?'9+':cartCount}</span>}
          </div>
          {isLoggedIn ? (
            <div className="pr-pw" onClick={e=>{e.stopPropagation();setShowProfileDrop(p=>!p);}}>
              <button className="pr-pb">
                <div className="pr-pav">{(currentUser.name||'U').charAt(0).toUpperCase()}</div>
                <span className="pr-pnm">{(currentUser.name||'').split(' ')[0]||currentUser.phone}</span>
              </button>
              {showProfileDrop && (
                <ProfileDropdown user={currentUser} onLogout={handleLogout}
                  onViewProfile={()=>{setShowProfileDrop(false);setShowProfile(true);}}/>
              )}
            </div>
          ) : (
            <button className="pr-lbtn" onClick={()=>setShowAuthModal(true)}>
              <UserIcon/>Login
            </button>
          )}
        </div>
      </div>
    </header>
  );

  const Footer = () => (
    <footer className="ct-footer">
      <div className="ct-footer-in">
        <div className="ct-footer-grid">
          <div>
            <div className="ct-flogo" onClick={()=>navigate('/')}>
              <img src="/images/logo.png" alt="Fresh Hills"
                onError={e=>{e.target.onerror=null;e.target.src='https://via.placeholder.com/40?text=FH';}}/>
            </div>
            <p className="ct-fabout">Kerala's trusted nadan milk store. Pure cow milk, fresh paneer, curd and more — delivered to your door every morning since 2024.</p>
            <div className="ct-fsoc">
              {['fb','tw','ig'].map(s=>(
                <a key={s} href="#"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width:'1rem',height:'1rem'}}><circle cx="12" cy="12" r="10" strokeWidth="2"/></svg></a>
              ))}
            </div>
          </div>
          <div>
            <h4 className="ct-fttl"><HeartIcon/> Quick Links</h4>
            <ul className="ct-flinks">
              {[['/',         'Home'],
                ['/products', 'Products'],
                ['/#offers',  'Offers'],
                ['/#contact', 'Contact']].map(([href,label])=>(
                <li key={label}><a href={href}><ChevronRightIcon style={{width:'.7rem'}}/>{label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="ct-fttl"><HeartIcon/> Our Products</h4>
            <ul className="ct-flinks">
              {['Fresh Milk','Paneer','Curd & Yogurt','Buttermilk','Ghee'].map(p=>(
                <li key={p}><a href="/products"><ChevronRightIcon style={{width:'.7rem'}}/>{p}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="ct-fttl"><HeartIcon/> Contact Us</h4>
            <ul className="ct-fcontact">
              <li><MapPinIcon/><span>36/845, Fesicon Enterprises, Near Sunrise Hospital, Sea Port - Air Port Road, Kakkanad, Ernakulam, Kerala, 682021</span></li>
              <li><DevicePhoneMobileIcon/><span>+91 70108 44160</span></li>
              <li>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{width:'1rem',height:'1rem',flexShrink:0}}>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
                <span>hello@fesiconenterprises.com</span>
              </li>
            </ul>
            <div className="ct-fnl">
              <p>Subscribe for offers</p>
              <div className="ct-fnl-row">
                <input type="email" placeholder="Your email address"/>
                <button>Go</button>
              </div>
            </div>
          </div>
        </div>
        <div className="ct-fbot">
          <div className="ct-fcopy">
            <span>© {currentYear} FESICON ENTERPRISE. All rights reserved. ❤️ Made in Kerala</span>
            <br/><span style={{fontSize:'.7rem',display:'block',marginTop:'.2rem'}}>GST: 32AAGFF6427Q1Z2</span>
          </div>
          <div className="ct-fbot-links">
            <a href="#">Privacy Policy</a>
            <a href="#">Terms of Service</a>
            <a href="https://seo9ine.com" target="_blank" rel="noopener noreferrer" className="ct-seo">
              SEO9INE <HeartIcon className="hb" style={{width:'.85rem',height:'.85rem',color:'#ef4444'}}/>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );

  if (cart.length === 0) return <EmptyCart/>;

  return (
    <>
      <style>{css}</style>
      {showAuthModal && <WhatsAppAuthModal onSuccess={handleAuthSuccess} onClose={()=>setShowAuthModal(false)}/>}
      {showProfile   && <CustomerProfile  onClose={()=>setShowProfile(false)}/>}
      <div style={{minHeight:'100vh',background:'#f9fafb'}}>
        <Header/>

        <div className="ct-wrap">
          <div className="ct-top">
            <button className="ct-bk" onClick={()=>navigate('/products')}>
              <ArrowLeftIcon/>Continue Shopping
            </button>
            <button className="ct-clr" onClick={handleClearCart}>Clear Cart</button>
          </div>

          <h1 className="ct-ttl">🛒 Shopping Cart</h1>

          <div className="ct-grid">
            {/* Items */}
            <div className="ct-items">
              {cart.map(item => {
                const imgSrc  = getImageSrc(item);
                const showImg = imgSrc && !imgErrors[item.id];
                return (
                  <div key={item.id} className="ct-item">
                    <div className="ct-img">
                      {showImg
                        ? <img src={imgSrc} alt={item.name} onError={()=>setImgErrors(p=>({...p,[item.id]:true}))}/>
                        : <span>{getEmoji(item)}</span>}
                    </div>
                    <div className="ct-info">
                      <div className="ct-name" title={item.name}>{item.name}</div>
                      <div className="ct-desc">{item.description||'Fresh dairy product'}</div>
                      <div className="ct-price">
                        <span className="ct-total">₹{item.price*(item.quantity||1)}</span>
                        <span className="ct-unit">(₹{item.price} each)</span>
                      </div>
                    </div>
                    <div className="ct-acts">
                      <div className="ct-qc">
                        <button className="ct-qb" onClick={()=>handleQuantityChange(item.id,(item.quantity||1)-1)}>
                          <MinusIcon style={{width:'.8rem',height:'.8rem'}}/>
                        </button>
                        <span className="ct-qn">{item.quantity||1}</span>
                        <button className="ct-qb" onClick={()=>handleQuantityChange(item.id,(item.quantity||1)+1)}>
                          <PlusIcon style={{width:'.8rem',height:'.8rem'}}/>
                        </button>
                      </div>
                      <button className="ct-rm" onClick={()=>handleRemove(item.id,item.name)}>
                        <TrashIcon style={{width:'1rem',height:'1rem'}}/>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Summary */}
            <div className="ct-sum">
              <div className="ct-sum-ttl">
                <ShoppingCartIcon/>Order Summary
              </div>
              <div style={{maxHeight:'16rem',overflowY:'auto',marginBottom:'.75rem'}}>
                {cart.map(item=>(
                  <div key={item.id} style={{display:'flex',justifyContent:'space-between',marginBottom:'.45rem',fontSize:'.85rem'}}>
                    <span style={{color:'#6b7280'}}>{item.name} × {item.quantity||1}</span>
                    <span style={{fontWeight:'700'}}>₹{item.price*(item.quantity||1)}</span>
                  </div>
                ))}
              </div>
              <hr style={{border:'none',borderTop:'2px solid #f3f4f6',margin:'.65rem 0'}}/>
              <div className="ct-sum-row"><span>Subtotal</span><span>₹{getCartTotal()}</span></div>
              <div className="ct-sum-row">
                <span>Delivery</span>
                <span style={{color:'#10b981',fontWeight:700}}>FREE</span>
              </div>
              <div className="ct-sum-tot">
                <span>Total</span>
                <span className="ct-sum-tot-amt">₹{getCartTotal()}</span>
              </div>
              <button className="ct-co-btn" onClick={handleCheckout}>
                Proceed to Checkout →
              </button>
              <div className="ct-free">
                <BoltIcon/>Free delivery on all orders
              </div>
            </div>
          </div>
        </div>

        {/* Trust bar */}
        <div className="ct-trust">
          <div className="ct-trust-in">
            {[{bg:'#d1fae5',ico:<BoltIcon style={{width:'.9rem',height:'.9rem',color:'#059669'}}/>,label:'Same Day Delivery'},
              {bg:'#dbeafe',ico:<CurrencyRupeeIcon style={{width:'.9rem',height:'.9rem',color:'#2563eb'}}/>,label:'Cashless Payment'},
              {bg:'#fef3c7',ico:<CubeIcon style={{width:'.9rem',height:'.9rem',color:'#d97706'}}/>,label:'Fresh Stock Daily'},
              {bg:'#fae8ff',ico:<UserGroupIcon style={{width:'.9rem',height:'.9rem',color:'#9333ea'}}/>,label:'10k+ Customers'},
            ].map(t=>(
              <div key={t.label} className="ct-trust-item">
                <div className="ct-trust-ico" style={{background:t.bg}}>{t.ico}</div>
                <span>{t.label}</span>
              </div>
            ))}
          </div>
        </div>

        <Footer/>
      </div>
    </>
  );
};

export default Cart;